import { HTMLProps, ReactElement } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { TextButton } from '@flipster/react/design-system/text-button';
import { stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import OnboardingModal from './OrderTutorialV2Modal';
import { useOrderTutorialV2FeatureTooltip } from './use-order-tutorial-v2-feature-tooltip';
import { useOrderTutorialV2NewFeatureTooltip } from './use-order-tutorial-v2-new-feature-tooltip';

export interface TooltipOptions extends HTMLProps<HTMLElement> {
  children: ReactElement;
}

const OnboardingTooltip = ({ children }: TooltipOptions) => {
  const overlay = useOverlay();

  const [discoverTooltipEnabled, setDiscoverTooltipEnabled] = useOrderTutorialV2FeatureTooltip();
  const [discoverNewFeatureTooltipEnabled, setDiscoverNewFeatureTooltipEnabled] = useOrderTutorialV2NewFeatureTooltip();

  const closeTradeOnboardingTooltip = () => {
    setDiscoverTooltipEnabled(false);
    setDiscoverNewFeatureTooltipEnabled(false);
  };

  const onClickContent = () => {
    segment.track(TradeEvent.TradingOnboardingTooltipClick, {
      tooltip_text: 'Learn how to place\nyour order in 1 minute',
    });
    closeTradeOnboardingTooltip();
    overlay.open(({ isOpen, onClose, unmount }) => (
      <OnboardingModal variant='full' isOpen={isOpen} onClose={onClose} onFinish={onClose} onExit={unmount} />
    ));
  };

  const onClickClose = () => {
    closeTradeOnboardingTooltip();
    segment.track(TradeEvent.TradingOnboardingTooltipCloseClick, {
      tooltip_text: 'Learn how to place\nyour order in 1 minute',
    });
  };

  const onClickNewFeature = () => {
    segment.track(TradeEvent.TradingOnboardingTooltipClick, {
      tooltip_text: 'Discover new feature',
    });
    closeTradeOnboardingTooltip();
    overlay.open(({ isOpen, onClose, unmount }) => (
      <OnboardingModal variant='new-features' isOpen={isOpen} onClose={onClose} onFinish={onClose} onExit={unmount} />
    ));
  };

  const onClickCloseNewFeature = () => {
    closeTradeOnboardingTooltip();
    segment.track(TradeEvent.TradingOnboardingTooltipCloseClick, {
      tooltip_text: 'Discover new feature',
    });
  };

  if (!discoverTooltipEnabled) {
    return (
      <Tooltip
        open={discoverNewFeatureTooltipEnabled}
        shape='gray'
        strategy='fixed'
        placement='left-start'
        onOpenChange={(o) => {
          if (o) {
            segment.track(TradeEvent.TradingOnboardingTooltipView, {
              tooltip_text: 'Discover new feature',
            });
          }
        }}
      >
        <Tooltip.Trigger>{children}</Tooltip.Trigger>
        <Tooltip.Content onClose={onClickCloseNewFeature} onClick={onClickNewFeature} portal>
          <FormattedMessage id='Autogen_common.r251126.s_Discover_new_feature' />
        </Tooltip.Content>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      open={discoverTooltipEnabled}
      shape='gray'
      strategy='fixed'
      placement='left-start'
      onOpenChange={(o) => {
        if (o) {
          segment.track(TradeEvent.TradingOnboardingTooltipView, {
            tooltip_text: 'Learn how to place\nyour order in 1 minute',
          });
        }
      }}
    >
      <Tooltip.Trigger>{children}</Tooltip.Trigger>
      <Tooltip.Content onClose={onClickClose} portal>
        <div className={stack({ gap: 12, align: 'flex-start' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Learn_how_to_place_your_order_in_1_minute' />
          <TextButton label={<FormattedMessage id='Trade.r260421.Learn_how_button' />} onClick={onClickContent} />
        </div>
      </Tooltip.Content>
    </Tooltip>
  );
};

export default OnboardingTooltip;
